import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle2, 
  User, 
  Phone, 
  Mail, 
  Award, 
  Users, 
  Zap, 
  Cpu, 
  Activity, 
  Settings, 
  Wrench, 
  FileText, 
  Anchor, 
  Activity as OffshoreIcon, 
  Heart, 
  Coffee, 
  Factory 
} from 'lucide-react';
import GalleryModal from '../components/GalleryModal';

export default function About() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function fetchPage() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/by-slug/da/om-carpa');
        if (res.ok) {
          const data = await res.json();
          setPageData(data);
        }
      } catch (err) {
        console.error('Error fetching about page:', err);
      }
    }
    fetchPage();
  }, []);

  const title = pageData?.title?.da || pageData?.title || "Om CARPA - CARPA ApS";
  const desc = pageData?.meta_description?.da || pageData?.meta_description || "Læs om CARPAs tekniske erfaring, hands-on tilgang og fokus på sikker drift og gennemførelse i komplekse industrielle projekter.";

  const branches = [
    { name: 'Forsyning', icon: Cpu, desc: 'Fjernvarme, vand, og el-infrastruktur.' },
    { name: 'Power-to-X', icon: Zap, desc: 'Brint- og e-fuel anlæg.' },
    { name: 'Raffinaderier', icon: Factory, desc: 'Procesanlæg og petrokemi.' },
    { name: 'Offshore', icon: OffshoreIcon, desc: 'Vind, olie og gas.' },
    { name: 'Marine', icon: Anchor, desc: 'Skibssystemer og fremdrift.' },
    { name: 'Pharma', icon: Heart, desc: 'Sterile procesmiljøer.' },
    { name: 'Fødevarer', icon: Coffee, desc: 'Hygiejniske proceslinjer.' },
    { name: 'Industri', icon: Settings, desc: 'Produktion og maskinanlæg.' }
  ];

  const competencies = [
    'Commissioning',
    'Operational Readiness',
    'Technical Leadership',
    'Strategic Maintenance',
    'Shutdown Management',
    'Handover & Documentation',
    'Power-to-X',
    'Process Industry'
  ];

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen font-sans antialiased">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[55vh] lg:min-h-[60vh] flex flex-col justify-center py-16 md:py-24 overflow-hidden bg-[#0a1520] border-b border-[#0e1f2f]/20 mb-16">
        {/* Background Grid Pattern & Image Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={pageData?.hero_images?.[0] || "https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/afea74fa-cf1a-45a7-81dc-6c431b572c30.jpeg"}
            alt="Om CARPA"
            className="h-full w-full object-cover opacity-35 lg:opacity-45 object-center"
          />
          {/* Subtle Technical Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(23,211,214,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(23,211,214,0.04)_1px,transparent_1px)] bg-[size:24px_24px]" />
          {/* Gradient Overlay preserving text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1520] via-[#0a1520]/95 to-transparent hidden lg:block" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1520]/90 via-[#0a1520]/80 to-[#0a1520] lg:hidden" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Title & Process */}
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Action/Process Header Link */}
              <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-mono tracking-wider uppercase">
                <span className="text-[#17d3d6] font-bold">
                  {pageData?.hero_badge?.da || pageData?.hero_badge || "Erfaring → Hands-on → Driftssikkerhed"}
                </span>
              </div>

              <div className="space-y-2">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-wide uppercase text-white leading-none">
                  {pageData?.hero_title?.da || pageData?.hero_title || "Om CARPA"}
                </h1>
                <p className="text-xl md:text-2xl font-medium tracking-tight text-slate-200">
                  {pageData?.hero_description?.da || pageData?.hero_description || "Teknisk tyngde & praktisk erfaring"}
                </p>
                <div className="w-16 h-[3px] bg-[#17d3d6] mt-4" />
              </div>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                {desc}
              </p>
            </div>

            {/* Right Column: Premium Floating Card with Glow Border */}
            <div className="lg:col-span-5">
              <div className="bg-[#041326]/65 backdrop-blur-md border border-[rgba(23,211,214,0.15)] rounded-xl p-6 lg:p-8 flex flex-col justify-between transition-all duration-300 hover:border-[#17d3d6]/40 shadow-2xl">
                <div>
                  <div className="flex items-center gap-4 pb-4 border-b border-slate-700/40">
                    <div className="w-12 h-12 shrink-0 rounded-full border border-[#17d3d6] flex items-center justify-center text-[#17d3d6]">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold leading-tight">Hvem er CARPA?</h3>
                      <p className="text-xs text-slate-400 font-mono tracking-wider uppercase mt-0.5">CARPA ApS</p>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3.5">
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Stifter Laust Kildevæld, Maskinmester
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Hands-on erfaring fra komplekse anlæg
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Specialister i PtX, forsyning & industri
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Fokus på struktur og driftssikkerhed
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 space-y-24">
        
        {/* Main Narrative Section */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0e1f2f] tracking-tight leading-tight">
                {pageData?.intro_title?.da || pageData?.intro_title || "Fra komplekse tekniske anlæg til stabil drift"}
              </h2>
              {pageData?.additional_content?.da ? (
                <div 
                  className="text-stone-600 text-sm sm:text-base leading-relaxed space-y-4 prose max-w-none prose-stone [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-[#0e1f2f] [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#0e1f2f] [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-4 [&_strong]:text-[#0e1f2f]"
                  dangerouslySetInnerHTML={{ __html: pageData.additional_content.da }}
                />
              ) : (
                <>
                  <p className="text-lg text-stone-600 leading-relaxed">
                    CARPA hjælper industri-, energi- og procesvirksomheder med at skabe struktur, fremdrift og teknisk kontrol i overgangen fra projekt til drift.
                  </p>
                  <p className="text-lg text-stone-600 leading-relaxed">
                    Vi kombinerer praktisk site-erfaring, commissioning-ledelse og teknisk forståelse med en hands-on tilgang til test, dokumentation, handover, drift og vedligehold.
                  </p>
                </>
              )}
            </div>

            {/* Vores Fokus Box */}
            <div className="bg-white rounded-2xl border border-[#0e1f2f]/10 p-6 md:p-8 shadow-sm space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-[#3c8888]" />
                <h3 className="text-xs font-bold text-[#0e1f2f] uppercase tracking-wider font-mono">
                  {pageData?.intro_badge?.da || pageData?.intro_badge || "Vores fokus"}
                </h3>
              </div>
              <p className="text-base sm:text-lg text-stone-800 leading-relaxed font-semibold">
                {pageData?.intro_description?.da || pageData?.intro_description || "At sikre, at tekniske anlæg kan drives sikkert, stabilt og effektivt efter overdragelse. Vi arbejder tæt på både projekt og drift og omsætter tekniske krav, dokumentation og praktiske erfaringer til løsninger, der fungerer i virkeligheden."}
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-xl overflow-hidden border border-[#0e1f2f]/10 shadow-md bg-white p-2 max-w-md mx-auto lg:mx-0">
              <img
                src={pageData?.intro_image?.[0] || "https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/ad3a5af6-09f1-4924-9433-0a99ce0d0394.webp"}
                alt="Laust Kildevæld"
                className="w-full h-auto rounded-lg object-cover"
              />
            </div>
          </div>
        </section>

        {/* Three Cards Section */}
        <section className="space-y-8">
          <div className="border-b border-[#0e1f2f]/10 pb-4">
            <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
              Vores Kernekompetencer
            </h3>
            <h4 className="text-2xl sm:text-3xl font-extrabold text-[#0e1f2f] tracking-tight mt-1">
              Hvordan vi skaber struktur
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Commissioning & Handover */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-8 shadow-sm flex flex-col gap-4">
              <div className="h-12 w-12 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center">
                <Settings className="h-6 w-6" />
              </div>
              <h5 className="text-xl font-bold text-[#0e1f2f]">Commissioning & Handover</h5>
              <p className="text-stone-600 text-sm leading-relaxed">
                Overblik over systemer, test, dokumentation, punch items og overdragelse. Vi sikrer, at alt er verificeret og godkendt.
              </p>
            </div>

            {/* Card 2: Operational Readiness */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-8 shadow-sm flex flex-col gap-4">
              <div className="h-12 w-12 rounded-lg bg-[#0e1f2f]/10 text-[#0e1f2f] flex items-center justify-center">
                <FileText className="h-6 w-6" />
              </div>
              <h5 className="text-xl font-bold text-[#0e1f2f]">Operational Readiness</h5>
              <p className="text-stone-600 text-sm leading-relaxed">
                Organisation, procedurer, vedligehold og dokumentation klar før drift. Vi klæder jeres team på til at overtage ejerskabet.
              </p>
            </div>

            {/* Card 3: Strategic Maintenance */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-8 shadow-sm flex flex-col gap-4">
              <div className="h-12 w-12 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center">
                <Wrench className="h-6 w-6" />
              </div>
              <h5 className="text-xl font-bold text-[#0e1f2f]">Strategic Maintenance</h5>
              <p className="text-stone-600 text-sm leading-relaxed">
                Vedligeholdsstruktur, reservedelsstrategi, CMMS og praktiske løsninger. Vi reducerer nedetid og optimerer levetiden.
              </p>
            </div>
          </div>
        </section>

        {/* Brancher Section */}
        <section className="space-y-8">
          <div className="border-b border-[#0e1f2f]/10 pb-4">
            <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
              Brancher & Sektorer
            </h3>
            <h4 className="text-2xl sm:text-3xl font-extrabold text-[#0e1f2f] tracking-tight mt-1">
              Hvem vi betjener
            </h4>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {branches.map((branch, idx) => {
              const Icon = branch.icon;
              return (
                <div 
                  key={idx}
                  className="bg-white rounded-xl border border-[#0e1f2f]/10 p-6 shadow-sm flex flex-col gap-3 hover:border-[#3c8888]/30 transition-colors"
                >
                  <div className="h-10 w-10 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h5 className="font-bold text-base text-[#0e1f2f]">{branch.name}</h5>
                    <p className="text-stone-500 text-xs mt-1 leading-relaxed">{branch.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Personprofil Section - Only Laust Kildevæld */}
        <section className="border-t border-[#0e1f2f]/10 pt-16 pb-16">
          <div className="mb-12 max-w-3xl">
            <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888] mb-2">
              Mennesket bag
            </h3>
            <h4 className="text-2xl sm:text-3xl font-extrabold text-[#0e1f2f] tracking-tight">
              Personprofil
            </h4>
          </div>

          <div className="bg-white rounded-2xl border border-[#0e1f2f]/10 p-6 md:p-10 shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left: Image & Contact */}
            <div className="lg:col-span-4 space-y-6">
              <div className="relative aspect-square rounded-xl overflow-hidden border border-[#0e1f2f]/10 shadow-sm bg-stone-100 max-w-[280px] mx-auto lg:mx-0">
                <img 
                  src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/ad3a5af6-09f1-4924-9433-0a99ce0d0394.webp" 
                  alt="Laust Kildevæld" 
                  className="h-full w-full object-cover object-top"
                />
              </div>
              
              <div className="space-y-1 text-center lg:text-left">
                <h5 className="text-2xl font-bold text-[#0e1f2f]">Laust Kildevæld</h5>
                <p className="text-sm font-mono text-[#3c8888] font-bold">Stifter | Maskinmester | Senior Consultant</p>
              </div>

              <div className="pt-4 border-t border-[#0e1f2f]/10 space-y-3 text-sm font-mono text-stone-600">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <Phone className="h-4 w-4 text-[#3c8888]" />
                  <a href="tel:+4553636223" className="hover:text-[#3c8888] transition-colors">+45 53 63 62 23</a>
                </div>
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <Mail className="h-4 w-4 text-[#3c8888]" />
                  <a href="mailto:llk@carpa.dk" className="hover:text-[#3c8888] transition-colors">llk@carpa.dk</a>
                </div>
              </div>

              <div className="pt-4 text-center lg:text-left">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center rounded-md bg-[#3c8888] px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-[#3c8888]/90 transition-colors border-b-2 border-[#3c8888]"
                >
                  Kontakt Laust
                </Link>
              </div>
            </div>

            {/* Right: Bio & Competencies */}
            <div className="lg:col-span-8 space-y-8">
              <div className="space-y-4 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Laust Kildevæld er uddannet maskinmester og stifter af CARPA ApS. Han har solid erfaring med teknisk ledelse, commissioning, drift, vedligehold og operational readiness på komplekse tekniske anlæg.
                </p>
                <p>
                  Hans erfaring spænder fra kraftværker, procesindustri, offshore og forsyning til Power-to-X, hvor han har arbejdet med projektgennemførelse, systemoverdragelse, shutdowns, dokumentation og driftssikring.
                </p>
                <p>
                  Laust arbejder hands-on og struktureret med at skabe fremdrift, overblik og løsninger, der fungerer i praksis — fra projektfase til stabil drift.
                </p>
              </div>

              {/* Competencies */}
              <div className="space-y-4 pt-6 border-t border-[#0e1f2f]/10">
                <h6 className="text-xs font-mono font-bold uppercase tracking-wider text-stone-500">
                  Kompetencer
                </h6>
                <div className="flex flex-wrap gap-2.5">
                  {competencies.map((comp, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center rounded-md bg-[#f7f8fa] border border-[#0e1f2f]/10 px-3 py-1.5 text-xs font-semibold text-[#0e1f2f]"
                    >
                      {comp}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
